import { useEffect, useState } from 'react'
import QRCode from 'qrcode'
import { formatDate } from '@/shared/util/dates'
import type { PromoCodeInfo } from './repository/accessGrantsRepository'
import {
  OFFERINGS,
  createPromoCode,
  normaliseCode,
  offeringLabel,
  promoUrl,
  updatePromoCode,
  type OfferingId,
  type PromoCodeFields,
} from './repository/promoCodeAdmin'

/**
 * A promo code: what Firestore holds, its link and QR code, and the form that
 * creates or edits it — 14 Oct 2026.
 *
 * The code is the document id, so the form lets it be typed once, at creation,
 * and never again. Everything else can change; the redemptions cannot.
 */

const isoDay = (iso: string | null) => (iso ? iso.slice(0, 10) : '')

/** The end of the chosen day in Oslo is near enough; the backend stores ISO. */
const endOfDay = (day: string) => (day ? new Date(`${day}T23:59:59`).toISOString() : null)

export function PromoCodeForm({
  initial,
  onSaved,
  onCancel,
}: {
  /** Absent: a new code. */
  initial?: PromoCodeInfo
  onSaved: (code: string) => void
  onCancel: () => void
}) {
  const editing = Boolean(initial)
  const [code, setCode] = useState(initial?.code ?? '')
  const [offering, setOffering] = useState<OfferingId>(
    (OFFERINGS.find((o) => o.id === initial?.offering)?.id ?? 'premium-promo-50') as OfferingId,
  )
  const [partner, setPartner] = useState(initial?.partner ?? '')
  const [limit, setLimit] = useState(String(initial?.limit ?? 0))
  const [active, setActive] = useState(initial?.active ?? true)
  const [duration, setDuration] = useState(initial?.durationDays ? String(initial.durationDays) : '')
  const [ends, setEnds] = useState(isoDay(initial?.expiresAt ?? null))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    const n = Number(limit || 0)
    const days = duration.trim() ? Number(duration) : null
    if (!editing && code.length < 3) return setError('A code is 3 to 32 letters and digits.')
    if (!Number.isInteger(n) || n < 0) return setError('The limit is a whole number; 0 means unlimited.')
    if (days !== null && (!Number.isInteger(days) || days < 1)) return setError('Access length is a whole number of days.')
    const fields: PromoCodeFields = {
      offering,
      partner: partner.trim(),
      limit: n,
      active,
      durationDays: days,
      expiresAt: endOfDay(ends),
    }
    setBusy(true)
    setError(null)
    try {
      if (editing) await updatePromoCode(initial!.code, fields)
      else await createPromoCode(code, fields)
      onSaved(editing ? initial!.code : code)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="pcode__form">
      <label className="pcode__field">
        <span className="pcode__label">Code</span>
        {editing ? (
          <span className="pcode__fixed" title="A code cannot be changed once it exists.">
            {initial!.code}
          </span>
        ) : (
          <input
            className="input"
            value={code}
            onChange={(e) => setCode(normaliseCode(e.target.value))}
            placeholder="SPRING50"
            autoFocus
          />
        )}
        <span className="micro muted">
          {editing
            ? 'The code itself cannot be changed. Make a new one instead.'
            : 'Letters A–Z and digits, 3–32 long. It cannot be changed afterwards.'}
        </span>
      </label>

      <div className="pcode__grid">
        <label className="pcode__field">
          <span className="pcode__label">Discount</span>
          <select className="input" value={offering} onChange={(e) => setOffering(e.target.value as OfferingId)}>
            {OFFERINGS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="pcode__field">
          <span className="pcode__label">Partner</span>
          <input className="input" value={partner} onChange={(e) => setPartner(e.target.value)} placeholder="Who the code is for" />
        </label>
        <label className="pcode__field">
          <span className="pcode__label">Redemption limit</span>
          <input className="input" type="number" min={0} value={limit} onChange={(e) => setLimit(e.target.value)} />
          <span className="micro muted">0 means unlimited.</span>
        </label>
        <label className="pcode__field">
          <span className="pcode__label">Access length (days)</span>
          <input className="input" type="number" min={1} value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="Optional" />
          <span className="micro muted">How long a redeemer keeps the discount.</span>
        </label>
        <label className="pcode__field">
          <span className="pcode__label">Code ends</span>
          <input className="input" type="date" value={ends} onChange={(e) => setEnds(e.target.value)} />
          <span className="micro muted">Optional. After this date the code is refused.</span>
        </label>
        <label className="pcode__check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — can be redeemed
        </label>
      </div>

      {error && <div className="empty-note">{error}</div>}

      <div className="pcode__actions">
        <button className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn--primary" onClick={() => void save()} disabled={busy}>
          {busy ? 'Saving…' : editing ? 'Save changes' : 'Create code'}
        </button>
      </div>
    </div>
  )
}

export function PromoCodeHeader({
  promo,
  canEdit,
  onChanged,
}: {
  promo: PromoCodeInfo
  canEdit: boolean
  onChanged: () => void
}) {
  const url = promoUrl(promo.code)
  const [qr, setQr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)

  useEffect(() => {
    let live = true
    QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 1, width: 320, color: { dark: '#000B38', light: '#FFFFFF' } })
      .then((d) => live && setQr(d))
      .catch(() => live && setQr(null))
    return () => {
      live = false
    }
  }, [url])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  if (editing) {
    return (
      <PromoCodeForm
        initial={promo}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false)
          onChanged()
        }}
      />
    )
  }

  const rows: [string, string][] = [
    ['Status', promo.active ? 'Active' : 'Inactive'],
    ['Discount', offeringLabel(promo.offering)],
    ['Offering', promo.offering || '—'],
    ['Partner', promo.partner || '—'],
    ['Redeemed', `${promo.redeemCount}${promo.limit ? ` of ${promo.limit}` : ' (no limit)'}`],
    ['Access length', promo.durationDays ? `${promo.durationDays} days` : '—'],
    ['Code ends', promo.expiresAt ? formatDate(promo.expiresAt) : '—'],
    ['Created', promo.createdAt ? `${formatDate(promo.createdAt)}${promo.createdBy ? ` · ${promo.createdBy}` : ''}` : '—'],
  ]
  if (promo.updatedAt) rows.push(['Changed', `${formatDate(promo.updatedAt)}${promo.updatedBy ? ` · ${promo.updatedBy}` : ''}`])

  return (
    <div className="pcode__head">
      <div className="pcode__info">
        <dl className="pcode__facts">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
        <div className="pcode__link">
          <a href={url} target="_blank" rel="noreferrer">
            {url}
          </a>
          <button className="btn btn--ghost pcode__sm" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
        {canEdit && (
          <button className="btn btn--outline pcode__sm" onClick={() => setEditing(true)}>
            Edit code
          </button>
        )}
      </div>
      {qr && (
        <a className="pcode__qr" href={qr} download={`skida-${promo.code}.png`} title="Download the QR code">
          <img src={qr} alt={`QR code for ${url}`} width={148} height={148} />
          <span className="micro muted">Download PNG</span>
        </a>
      )}
    </div>
  )
}
